import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import { useCreateQCStandard, useQCStandardQuery } from "../api/queries";
import { QCFormTour, useQCFormTour } from "../tour/QCFormTour";
import type { QCFormValues } from "../ui/QCForm";
import { QCForm } from "../ui/QCForm";

export function QCCreatePage() {
  const nav = useNavigate({ from: "/app/qc/new" });
  const search = useSearch({ from: "/app/qc/new" });
  const createQC = useCreateQCStandard();
  const tour = useQCFormTour();

  const copyFromId = (search as { copyFrom?: string })?.copyFrom;
  const sourceQC = useQCStandardQuery(copyFromId || "");

  const initialValues = useMemo<Partial<QCFormValues> | undefined>(() => {
    if (!sourceQC.data || !copyFromId || !sourceQC.isSuccess) return undefined;

    const qc = sourceQC.data;
    return {
      name: `${qc.name} (ສໍາເນົາ)`,
      description: qc.description,
      workTypeId: qc.workTypeId,
      workStage: qc.workStage ?? null,
      departmentId: qc.departmentId ?? null,
      inspectorPositionId: qc.inspectorPositionId ?? null,
      inspectorUserIds: qc.inspectors?.map((i) => i.userId) ?? [],
      status: "draft",
      requirePhoto: qc.requirePhoto,
      requireNotesOnFail: qc.requireNotesOnFail,
      passCriteria: qc.passCriteria ?? null,
      failCriteria: qc.failCriteria ?? null,
      actionOnFail: qc.actionOnFail ?? null,
      notifyPositionId: qc.notifyPositionId ?? null,
      checklistId: qc.checklistId ?? null,
      kpiId: qc.kpiId ?? null,
      items:
        qc.items?.map((item) => ({
          title: item.title,
          description: item.description ?? null,
          orderIndex: item.orderIndex,
          isRequired: item.isRequired,
          passCriteria: item.passCriteria ?? null,
          failCriteria: item.failCriteria ?? null,
        })) ?? [],
    };
  }, [sourceQC.data, sourceQC.isSuccess, copyFromId]);

  return (
    <>
      <Header />

      <Main>
        <QCFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">ສ້າງມາດຕະຖານ QC ໃໝ່</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ສ້າງມາດຕະຖານ QC ໃໝ່ສໍາລັບການນໍາໃຊ້
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  nav({ to: "/app/qc" });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* Create Form Section */}
          <div className="rounded-xl border bg-card p-6">
            <QCForm
              initialValues={initialValues}
              onSubmit={async (vals) => {
                await createQC.mutateAsync({
                  name: vals.name,
                  description: vals.description ?? undefined,
                  workTypeId: vals.workTypeId,
                  workStage: vals.workStage ?? undefined,
                  departmentId: vals.departmentId ?? undefined,
                  inspectorPositionId: vals.inspectorPositionId ?? undefined,
                  inspectorUserIds: vals.inspectorUserIds ?? [],
                  status: vals.status,
                  requirePhoto: vals.requirePhoto,
                  requireNotesOnFail: vals.requireNotesOnFail,
                  passCriteria: vals.passCriteria ?? undefined,
                  failCriteria: vals.failCriteria ?? undefined,
                  actionOnFail: vals.actionOnFail ?? undefined,
                  notifyPositionId: vals.notifyPositionId ?? undefined,
                  checklistId: vals.checklistId ?? undefined,
                  kpiId: vals.kpiId ?? undefined,
                  items: vals.items?.map((item, index) => ({
                    ...item,
                    orderIndex: item.orderIndex ?? index,
                  })),
                });
                nav({ to: "/app/qc" });
              }}
              submitting={createQC.isPending}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
